// =============================================================================
// Formatação — pt-BR, e dinheiro sem passar por float
//
// A API devolve dinheiro como STRING de centavos, de propósito (api/src/db.ts):
// `bigint` em `Number` perde centavos acima de 2^53. Aqui a conversão para texto
// é feita com `BigInt`, então o valor exibido é exato em qualquer magnitude —
// não "exato o bastante para os nossos valores".
// =============================================================================

const AGRUPADOR = new Intl.NumberFormat("pt-BR");

/** "1234567" (centavos) → "R$ 12.345,67" */
export function moeda(centavos: string | null | undefined): string {
  if (centavos === null || centavos === undefined || centavos === "") return "—";
  const n = BigInt(centavos);
  const negativo = n < 0n;
  const abs = negativo ? -n : n;
  const reais = abs / 100n;
  const resto = abs % 100n;
  const inteiro = AGRUPADOR.format(reais);
  const dec = resto.toString().padStart(2, "0");
  return `${negativo ? "-" : ""}R$ ${inteiro},${dec}`;
}

/** Versão curta para eixo de gráfico, onde não cabe o valor inteiro. */
export function moedaCompacta(centavos: string | null | undefined): string {
  if (centavos === null || centavos === undefined || centavos === "") return "—";
  const reais = Number(BigInt(centavos) / 100n);
  const abs = Math.abs(reais);
  const sinal = reais < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sinal}R$ ${(abs / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (abs >= 1_000) return `${sinal}R$ ${Math.round(abs / 1_000)}k`;
  return `${sinal}R$ ${AGRUPADOR.format(abs)}`;
}

/** Centavos como número, para alimentar o eixo do gráfico. Só para desenho. */
export function emReais(centavos: string | null | undefined): number {
  if (centavos === null || centavos === undefined || centavos === "") return 0;
  return Number(BigInt(centavos)) / 100;
}

/** `numeric(7,2)` chega como string. "83.50" → "83,5%" */
export function percentual(v: string | number | null | undefined, casas = 1): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number.parseFloat(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
}

export function numero(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number.parseFloat(v);
  return Number.isFinite(n) ? AGRUPADOR.format(n) : "—";
}

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const MESES_CURTOS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/**
 * Competência chega como "2026-09-01". Formatamos por corte de string, sem
 * `new Date`: a data vira instante UTC e, exibida em America/Sao_Paulo, volta
 * como 31/08 — o mês inteiro erra por um dia (CLAUDE.md §12).
 */
export function competenciaLonga(comp: string): string {
  const [ano, mes] = comp.split("-");
  const i = Number(mes) - 1;
  return `${MESES[i] ?? mes}/${ano}`;
}

export function competenciaCurta(comp: string): string {
  const [ano, mes] = comp.split("-");
  const i = Number(mes) - 1;
  return `${MESES_CURTOS[i] ?? mes}/${(ano ?? "").slice(2)}`;
}

/**
 * A competência anterior — "2026-01-01" → "2025-12-01".
 *
 * ⚠️ Aritmética de TEXTO, nunca `Date`. `new Date("2026-01-01")` é meia-noite
 * UTC, e num fuso a leste disso já é dia 31 do mês anterior: a competência
 * andaria um mês sozinha, sem erro nenhum. É a mesma armadilha que o corte de
 * competência por `Date` criou em 16/09 e que o teste da suíte guarda rodando
 * em `TZ=Pacific/Kiritimati`.
 */
export function competenciaAnterior(comp: string): string {
  const ano = Number(comp.slice(0, 4));
  const mes = Number(comp.slice(5, 7));
  return mes === 1 ? `${ano - 1}-12-01` : `${ano}-${String(mes - 1).padStart(2, "0")}-01`;
}

export function mesCurto(comp: string): string {
  const i = Number(comp.split("-")[1]) - 1;
  return MESES_CURTOS[i] ?? comp;
}

/** "2026-09-15" → "15/09". Datas de lançamento, sem `new Date` pelo mesmo motivo. */
export function dataCurta(d: string | null | undefined): string {
  if (!d) return "—";
  const [, mes, dia] = d.split("-");
  return dia && mes ? `${dia}/${mes}` : d;
}

/** Os 7 estados do AF-01, com o rótulo que o Financeiro usa. */
export const ROTULO_STATUS: Record<string, string> = {
  provisionado: "Provisionado",
  comprometido: "Comprometido",
  realizado: "Realizado",
  pico_justificado: "Pico justificado",
  excedente_aguardando_ajuste: "Excedente — aguardando ajuste",
  excedente: "Excedente",
  nao_orcado: "Não orçado",
  // Não é um dos 7 estados de linha: marca, no Painel, o gasto de categoria
  // COM orçamento cujo lançamento ainda não foi atribuído a uma linha.
  aguardando_alocacao: "Aguardando alocação",
};

export type Tom = "ok" | "atencao" | "risco" | "neutro";

export const TOM_STATUS: Record<string, Tom> = {
  provisionado: "neutro",
  comprometido: "neutro",
  realizado: "ok",
  pico_justificado: "atencao",
  excedente_aguardando_ajuste: "risco",
  excedente: "risco",
  nao_orcado: "atencao",
  aguardando_alocacao: "neutro",
};
